import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, AlertTriangle, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const formatR = (v) => `R ${Number(v || 0).toFixed(2)}`;

export default function DuplicateSuppliersDialog({ open, onClose, onDeleted }) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/suppliers/duplicates`);
      setGroups(r.data.duplicate_groups || []);
    } catch (e) {
      toast.error("Failed to load duplicate suppliers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setConfirming(false);
      setConfirmText("");
      fetchDuplicates();
    }
  }, [open]);

  const toggleId = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const summary = useMemo(() => {
    let grvs = 0,
      pmts = 0,
      jrnls = 0,
      bal = 0;
    for (const g of groups) {
      for (const m of g.members) {
        if (selectedIds.has(m.id)) {
          grvs += m.grv_count || 0;
          pmts += m.payment_count || 0;
          jrnls += m.journal_count || 0;
          bal += m.balance || 0;
        }
      }
    }
    return { grvs, pmts, jrnls, bal };
  }, [groups, selectedIds]);

  const handleDelete = async () => {
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Type DELETE in capitals to confirm');
      return;
    }
    setDeleting(true);
    try {
      const r = await axios.post(`${API}/api/suppliers/hard-delete`, {
        supplier_ids: Array.from(selectedIds),
        confirmation: "DELETE",
      });
      const deleted = r.data?.deleted_count ?? 0;
      const results = r.data?.results || [];
      const totals = results.reduce(
        (acc, x) => {
          if (x.status === "deleted") {
            acc.grvs += x.removed.grvs || 0;
            acc.pmts += x.removed.supplier_payments || 0;
            acc.jrnls += x.removed.supplier_journals || 0;
            acc.pos += x.removed.purchase_orders || 0;
          }
          return acc;
        },
        { grvs: 0, pmts: 0, jrnls: 0, pos: 0 }
      );
      toast.success(
        `Permanently deleted ${deleted} supplier(s). Removed ${totals.grvs} GRVs, ${totals.pmts} payments, ${totals.jrnls} journals, ${totals.pos} POs.`
      );
      setSelectedIds(new Set());
      setConfirming(false);
      setConfirmText("");
      await fetchDuplicates();
      if (onDeleted) onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="duplicate-suppliers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-red-600" />
            Find &amp; Delete Duplicate Suppliers
          </DialogTitle>
          <DialogDescription>
            Suppliers sharing the same name (case- and punctuation-insensitive) are grouped below.
            Select the duplicates you want to <strong className="text-red-700">permanently delete</strong> &mdash;
            this also removes ALL their GRVs, purchase orders, payments and journals from the system. There is no undo.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Scanning for duplicates&hellip;</span>
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            No duplicate supplier names found. Your supplier list is clean. 🎉
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.normalized_name} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`duplicate-group-${g.normalized_name}`}>
                <div className="bg-amber-50 px-4 py-2 border-b border-amber-200 flex items-center justify-between">
                  <div className="font-semibold text-amber-900">
                    {g.display_name}{" "}
                    <span className="text-xs text-amber-700 font-normal">
                      ({g.member_count} duplicates)
                    </span>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2 w-10"></th>
                      <th className="text-left px-3 py-2">Supplier ID</th>
                      <th className="text-right px-3 py-2">Balance</th>
                      <th className="text-right px-3 py-2">Opening</th>
                      <th className="text-right px-3 py-2">GRVs</th>
                      <th className="text-right px-3 py-2">Payments</th>
                      <th className="text-right px-3 py-2">Journals</th>
                      <th className="text-left px-3 py-2">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.members.map((m, idx) => {
                      const isFirst = idx === 0;
                      const isChecked = selectedIds.has(m.id);
                      return (
                        <tr
                          key={m.id}
                          className={`border-t border-gray-100 ${isChecked ? "bg-red-50" : ""}`}
                          data-testid={`duplicate-row-${m.id}`}
                        >
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => toggleId(m.id)}
                              data-testid={`duplicate-select-${m.id}`}
                            />
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {m.id.slice(0, 8)}…
                            {isFirst && (
                              <span className="ml-2 text-[10px] uppercase tracking-wide text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                                most active
                              </span>
                            )}
                          </td>
                          <td className={`px-3 py-2 text-right font-semibold ${m.balance > 0.01 ? "text-red-700" : m.balance < -0.01 ? "text-green-700" : "text-gray-500"}`}>
                            {formatR(m.balance)}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-600">{formatR(m.opening_balance)}</td>
                          <td className="px-3 py-2 text-right">
                            {m.grv_count}{" "}
                            <span className="text-xs text-gray-500">({formatR(m.grv_total)})</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {m.payment_count}{" "}
                            <span className="text-xs text-gray-500">({formatR(m.payment_total)})</span>
                          </td>
                          <td className="px-3 py-2 text-right">{m.journal_count}</td>
                          <td className="px-3 py-2 text-xs">
                            {m.is_active ? (
                              <span className="text-green-700">Active</span>
                            ) : (
                              <span className="text-gray-400">Inactive</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {selectedIds.size > 0 && (
          <div className="border-2 border-red-300 bg-red-50 rounded-lg p-4 mt-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-red-800">
                  You are about to PERMANENTLY DELETE {selectedIds.size} supplier(s).
                </p>
                <p className="text-red-700 mt-1">
                  This will also remove:&nbsp;
                  <strong>{summary.grvs}</strong> GRVs,&nbsp;
                  <strong>{summary.pmts}</strong> payments,&nbsp;
                  <strong>{summary.jrnls}</strong> journals,&nbsp;
                  plus all linked purchase orders. The selected suppliers carry a combined balance of{" "}
                  <strong>{formatR(summary.bal)}</strong>. There is <strong>NO UNDO</strong>.
                </p>
                {confirming && (
                  <div className="mt-3">
                    <label className="text-xs font-semibold text-red-800 uppercase tracking-wide">
                      Type DELETE to confirm
                    </label>
                    <Input
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="DELETE"
                      className="mt-1 max-w-xs border-red-400 focus:border-red-600"
                      data-testid="duplicate-delete-confirm-input"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="duplicate-dialog-close-btn">
            Close
          </Button>
          {selectedIds.size > 0 && !confirming && (
            <Button
              variant="destructive"
              onClick={() => setConfirming(true)}
              data-testid="duplicate-start-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete {selectedIds.size} permanently
            </Button>
          )}
          {confirming && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting || confirmText.trim().toUpperCase() !== "DELETE"}
              data-testid="duplicate-confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Confirm permanent delete
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
